import { Page } from "@/components/page-layout";
import { PageEyebrow } from "@/components/page-eyebrow";
import { ProductTierBadge } from "@/components/product-tier-badge";
import { productTierColors } from "@/components/product-tier-utils";
import { Card, Cards, CardSkeleton } from "@/components/ui/Card";
import { Heading } from "@/components/ui/Heading";
import { Skeleton } from "@/components/ui/Skeleton";
import { SimpleTooltip } from "@/components/ui/Tooltip";
import { Text } from "@/components/ui/Text";
import { useSdkClient } from "@/contexts/Sdk";
import { useTelemetry } from "@/contexts/Telemetry";
import { ProductTier, useProductTier } from "@/hooks/useProductTier";
import { openSafeExternalUrl } from "@/lib/safe-external-url";
import { getServerURL } from "@/lib/utils";
import { TierLimits } from "@gram/client/models/components/tierlimits.js";
import { useGetCreditUsage } from "@gram/client/react-query/getCreditUsage.js";
import { useGetPeriodUsage } from "@gram/client/react-query/getPeriodUsage.js";
import { useGetUsageTiers } from "@gram/client/react-query/getUsageTiers.js";
import { PolarEmbedCheckout } from "@polar-sh/checkout/embed";
import { Button } from "@/components/ui/Button";
import { Stack } from "@/components/ui/Stack";
import { cn } from "@/lib/utils";
import { Info } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PlatformAdminOnlyPanel } from "@/components/platform-admin-only-panel";
import { RequireScope } from "@/components/require-scope";
import { BillingEmailSection } from "@/components/billing/billing-email-section";
import {
  PaygCapReachedBanners,
  PaygPaymentFailedBanner,
} from "@/components/billing/billing-banners";
import { InferenceCapsSection } from "@/components/billing/inference-caps-section";
import { PaygPlanSection } from "@/components/billing/payg-plan-section";
import { PaygPriceList } from "@/components/billing/payg-price-list";
import { TopUpCTA, UsageProgress } from "@/components/billing/usage-controls";
import { MeterUsageSection } from "@/components/billing/meter-usage-section";
import { SpendBreakdownSection } from "@/components/billing/spend-breakdown-section";
import { usePaygCheckoutAccess } from "@/components/billing/payg-checkout-access";
import { paygPlanState } from "@/components/billing/payg-plan-state";
import { useStripeSubscription } from "@/components/billing/use-stripe-subscription";
import { isNotFoundError } from "@/lib/route-errors";

export default function Billing(): JSX.Element {
  return (
    <Page>
      <Page.Header>
        <Page.Header.Breadcrumbs />
      </Page.Header>
      {/* A failed payment is what stops the whole account, so it precedes the
          cap notices when both states apply. The global header suppresses its
          cap banners on this route to avoid a duplicate query and notice. */}
      <Page.Banner>
        <PaygPaymentFailedBanner />
        <PaygCapReachedBanners />
      </Page.Banner>
      <Page.Body>
        <RequireScope scope={["org:read", "org:admin"]} level="page">
          <PageEyebrow className="mt-3 -mb-4" />
          <BillingInner />
        </RequireScope>
      </Page.Body>
    </Page>
  );
}

function BillingInner() {
  const productTier = useProductTier();
  const { eligible: canStartCheckout, trialLifecycle } = usePaygCheckoutAccess(
    productTier === "payg" ? "unsubscribed-payg" : "active-trial",
  );
  const activeTrial = trialLifecycle === "active";
  const { eligible: canStartBaseCheckout } =
    usePaygCheckoutAccess("unsubscribed-base");
  const subscription = useStripeSubscription({
    enabled:
      !activeTrial &&
      (productTier === "payg" ||
        (productTier === "base" && canStartBaseCheckout)),
  });

  let paymentRequired = activeTrial && canStartCheckout;
  if (productTier === "payg" && !activeTrial) {
    // Confirmed absence outranks stale data. An outage alone is not evidence
    // that payment is required, but it must not hide a known payment failure.
    if (isNotFoundError(subscription.error)) {
      paymentRequired = canStartCheckout;
    } else if (subscription.data) {
      paymentRequired =
        subscription.data.paymentFailed === true ||
        paygPlanState(subscription.data).kind === "inactive";
    }
  }

  if (productTier === "base" && !activeTrial) {
    paymentRequired =
      canStartBaseCheckout &&
      !subscription.isFetching &&
      !subscription.data &&
      subscription.isError &&
      isNotFoundError(subscription.error);
  }

  const baseSubscriptionError =
    productTier === "base" &&
    !activeTrial &&
    canStartBaseCheckout &&
    subscription.isError &&
    !isNotFoundError(subscription.error);

  const paymentGroup = (activeTrial ||
    baseSubscriptionError ||
    productTier === "payg" ||
    paymentRequired) && (
    <div
      key="payment"
      className={cn(
        "grid min-w-0 grid-cols-1 gap-x-8",
        activeTrial && canStartCheckout && "@3xl/main:grid-cols-2",
      )}
    >
      {baseSubscriptionError ? (
        <Page.Section>
          <Page.Section.Title area="">Payment</Page.Section.Title>
          <Page.Section.Body>
            <Stack direction="horizontal" align="center" gap={3}>
              <Text muted small role="alert">
                Couldn't load your subscription.
              </Text>
              <Button
                variant="secondary"
                size="sm"
                disabled={subscription.isFetching}
                onClick={() => void subscription.refetch()}
              >
                {subscription.isFetching ? "RETRYING..." : "RETRY"}
              </Button>
            </Stack>
          </Page.Section.Body>
        </Page.Section>
      ) : (
        <PaygPlanSection />
      )}
      <PaygPriceList />
    </div>
  );
  const usage = <BillingUsage key="usage" productTier={productTier} />;

  return (
    <>
      {paymentRequired ? [paymentGroup, usage] : [usage, paymentGroup]}
      {/* Enterprise contracts handle notifications through their contract. */}
      {productTier === "payg" && <BillingEmailSection />}
    </>
  );
}

function BillingUsage({ productTier }: { productTier: ProductTier }) {
  if (productTier === "enterprise" || productTier === "payg") {
    return (
      <>
        <SpendBreakdownSection />
        <MeterUsageSection />
        {/* Enterprise inference caps render only during an active trial. */}
        <InferenceCapsSection />
      </>
    );
  }

  return (
    <>
      {/* Legacy self-serve tiers still meter against Polar period usage. */}
      <UsageSection />
      {(productTier === "base_PAID" || productTier === "__deprecated__pro") && (
        <UsageTiers />
      )}
    </>
  );
}

const UsageSection = () => {
  const productTier = useProductTier();

  const { data: creditUsage } = useGetCreditUsage();
  const { data: periodUsage } = useGetPeriodUsage(undefined, undefined, {
    throwOnError: !getServerURL().includes("localhost"),
  });

  const UsageItem = ({
    label,
    tooltip,
    value,
    included,
    overageIncrement,
    noMax,
  }: {
    label: string;
    tooltip: string;
    value: number;
    included: number;
    overageIncrement: number;
    noMax?: boolean;
  }) => {
    return (
      <Stack gap={3} className="mb-6">
        <Stack direction="horizontal" align="center" gap={1}>
          <Text variant="body" className="font-medium">
            {label}
          </Text>
          <SimpleTooltip tooltip={tooltip}>
            <Info className="text-muted-foreground h-4 w-4" />
          </SimpleTooltip>
        </Stack>
        <UsageProgress
          value={value}
          included={included}
          overageIncrement={overageIncrement}
          noMax={noMax}
        />
      </Stack>
    );
  };

  return (
    <Page.Section>
      <Page.Section.Title area="">Usage</Page.Section.Title>
      <Page.Section.Description>
        A summary of your organization's usage this period. Please visit the
        billing portal to see complete details or manage your account.
      </Page.Section.Description>
      <RequireScope scope="org:admin" level="section">
        <TopUpCTA />
      </RequireScope>
      <Page.Section.Body>
        <div className="space-y-4">
          {periodUsage ? (
            <>
              <UsageItem
                label="Tool Calls"
                tooltip="The number of tool calls processed this period across all your organization's MCP servers."
                value={periodUsage.toolCalls}
                included={periodUsage.includedToolCalls || 1000}
                overageIncrement={periodUsage.includedToolCalls}
                noMax={productTier === "enterprise"}
              />
              <UsageItem
                label="Servers"
                tooltip="The number of MCP servers enabled across your organization. Note that this shows the current number of enabled servers, but you will be billed on the maximum number active simultaneously during the billing period."
                value={periodUsage.actualEnabledServerCount}
                included={periodUsage.includedServers || 1}
                overageIncrement={1}
                noMax={productTier === "enterprise"}
              />
            </>
          ) : (
            <>
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-full" />
            </>
          )}
          {creditUsage ? (
            <UsageItem
              label="Chat Based Credits"
              tooltip="The number of credits used this month for chat based products and other AI-powered dashboard experiences."
              value={creditUsage.creditsUsed}
              included={creditUsage.monthlyCredits}
              overageIncrement={creditUsage.monthlyCredits}
            />
          ) : (
            <>
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-full" />
            </>
          )}
          {periodUsage?.credits != null &&
            periodUsage.includedCredits != null && (
              <div className="pt-4">
                <PlatformAdminOnlyPanel>
                  <UsageItem
                    label="Chat Based Credits (Polar)"
                    tooltip="The number of credits used this month for chat based products and other AI-powered dashboard experiences."
                    value={periodUsage.credits}
                    included={periodUsage.includedCredits}
                    overageIncrement={periodUsage.includedCredits}
                    noMax={productTier === "enterprise"}
                  />
                </PlatformAdminOnlyPanel>
              </div>
            )}
        </div>
      </Page.Section.Body>
    </Page.Section>
  );
};

const UsageTiers = () => {
  const productTier = useProductTier();
  const { data: usageTiers, isLoading } = useGetUsageTiers();
  const client = useSdkClient();
  const telemetry = useTelemetry();
  const [checkoutLink, setCheckoutLink] = useState("");
  const [checkoutError, setCheckoutError] = useState(false);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(true);

  useEffect(() => {
    const fetchCheckoutLink = async () => {
      try {
        const link = await client.usage.createCheckout();
        if (!link) {
          console.error("Failed to create checkout link: received empty link");
          telemetry.capture("checkout_link_error", {
            error: "Received empty checkout link",
            accountType: productTier,
          });
          setCheckoutError(true);
          return;
        }
        setCheckoutLink(link);
        PolarEmbedCheckout.init();
      } catch (error) {
        console.error("Error creating checkout link:", error);
        telemetry.capture("checkout_link_error", {
          error:
            error instanceof Error
              ? error.message
              : "Failed to create checkout link",
          accountType: productTier,
        });
        setCheckoutError(true);
      } finally {
        setIsLoadingCheckout(false);
      }
    };

    void fetchCheckoutLink();
  }, [client, telemetry, productTier]);

  const handleFallbackClick = useCallback(() => {
    telemetry.capture("checkout_fallback_clicked", {
      accountType: productTier,
    });
  }, [telemetry, productTier]);

  const UpgradeCTA = useMemo(() => {
    if (checkoutError) {
      return (
        <Page.Section.CTA>
          <div className="isolate">
            <Button asChild variant="primary">
              <a
                href="mailto:gram@speakeasyapi.dev?subject=Upgrade%20Account"
                className="inline-flex"
                onClick={handleFallbackClick}
              >
                ADD CARD
              </a>
            </Button>
          </div>
        </Page.Section.CTA>
      );
    }

    return (
      <Page.Section.CTA>
        {/* Isolate is needed to get the rainbow working */}
        <div className="isolate">
          <Button disabled={isLoadingCheckout} asChild variant="primary">
            <a
              href={checkoutLink}
              data-polar-checkout
              data-polar-checkout-theme={"light"}
              className="inline-flex"
            >
              ADD CARD
            </a>
          </Button>
        </div>
      </Page.Section.CTA>
    );
  }, [checkoutLink, checkoutError, isLoadingCheckout, handleFallbackClick]);

  const polarPortalCTA = (
    <Page.Section.CTA>
      <Button
        onClick={() => {
          void (async () => {
            try {
              const link = await client.usage.createCustomerSession();
              if (!link) {
                console.error(
                  "Failed to create customer session: received empty link",
                );
                telemetry.capture("customer_session_error", {
                  error: "Received empty customer session link",
                  accountType: productTier,
                });
                return;
              }
              openSafeExternalUrl(link);
            } catch (error) {
              console.error("Error creating customer session:", error);
              telemetry.capture("customer_session_error", {
                error:
                  error instanceof Error
                    ? error.message
                    : "Failed to create customer session",
                accountType: productTier,
              });
            }
          })();
        }}
        disabled={productTier === "enterprise"}
      >
        MANAGE BILLING
      </Button>
    </Page.Section.CTA>
  );

  if (!usageTiers) {
    return <Cards isLoading={true} />;
  }

  const UsageCard = ({
    tier,
    tierLimits,
    active,
    previousTier,
  }: {
    tier: ProductTier;
    tierLimits: TierLimits;
    active: boolean;
    previousTier?: ProductTier;
  }) => {
    const price =
      tier === "enterprise"
        ? "Tailored pricing"
        : `$${tierLimits.basePrice.toLocaleString()}`;

    const ringColor = productTierColors(tier).ring;

    return (
      <Card className={cn("w-full p-6", active && `ring-2 ${ringColor}`)}>
        <Card.Header>
          <Card.Title>
            <Stack gap={1}>
              <ProductTierBadge tierOverride={tier} />
              <Heading variant="h2">{price}</Heading>
            </Stack>
          </Card.Title>
        </Card.Header>
        <Card.Content>
          <Stack gap={8}>
            <Stack gap={1}>
              <Text
                mono
                muted
                small
                variant="subheading"
                className="font-medium uppercase"
              >
                {previousTier
                  ? `Everything from ${previousTier}, plus`
                  : "Features"}
              </Text>
              <ul className="list-inside space-y-1">
                {tierLimits.featureBullets.map((bullet) => (
                  <li key={bullet}>
                    <span className="text-muted-foreground/60">✓</span> {bullet}
                  </li>
                ))}
              </ul>
            </Stack>
            {tierLimits.includedBullets &&
              tierLimits.includedBullets.length > 0 && (
                <Stack gap={1}>
                  <Text
                    mono
                    muted
                    small
                    variant="subheading"
                    className="font-medium uppercase"
                  >
                    Included
                  </Text>
                  <ul className="list-inside space-y-1">
                    {tierLimits.includedBullets.map((bullet) => (
                      <li key={bullet}>
                        <span className="text-muted-foreground/60">✓</span>{" "}
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </Stack>
              )}
            {tierLimits.addOnBullets && tierLimits.addOnBullets.length > 0 && (
              <Stack gap={1}>
                <Text
                  mono
                  muted
                  small
                  variant="subheading"
                  className="font-medium uppercase"
                >
                  Extras
                </Text>
                <ul className="list-inside space-y-1">
                  {tierLimits.addOnBullets.map((bullet) => (
                    <li key={bullet}>
                      <span className="text-muted-foreground/60">✓</span>{" "}
                      {bullet}
                    </li>
                  ))}
                </ul>
              </Stack>
            )}
          </Stack>
        </Card.Content>
      </Card>
    );
  };

  return (
    <Page.Section>
      {/* Secondary section below Usage: suppress the area eyebrow. */}
      <Page.Section.Title area="">Pricing</Page.Section.Title>
      <Page.Section.Description>
        A breakdown of our pricing tiers.
      </Page.Section.Description>
      <RequireScope scope="org:admin" level="section">
        {productTier === "base" ? UpgradeCTA : polarPortalCTA}
      </RequireScope>
      <Page.Section.Body>
        <Stack direction={"horizontal"} gap={4}>
          {isLoading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : (
            <>
              {/* Show the paid base tier card only if the user is on the tier (otherwise it's deprecated) */}
              {productTier === "base_PAID" && (
                <UsageCard
                  tier="base_PAID"
                  tierLimits={usageTiers.free}
                  active={productTier === "base_PAID"}
                />
              )}
              {/* Keep this so we can show it to users who are still on the old pricing tier */}
              {productTier === "__deprecated__pro" && (
                <UsageCard
                  tier="__deprecated__pro"
                  tierLimits={usageTiers.pro}
                  previousTier="base"
                  active={productTier === "__deprecated__pro"}
                />
              )}
              <UsageCard
                tier="enterprise"
                tierLimits={usageTiers.enterprise}
                previousTier="base"
                active={productTier === "enterprise"}
              />
            </>
          )}
        </Stack>
      </Page.Section.Body>
    </Page.Section>
  );
};
